import {
    ProductParametersProxy,
    ProductProxy,
    ProductWriteOffInput,
    StorageProductRelationHistoryProxy,
    StorageProxy,
    TechnologicalMapProductRelationProxy,
    TechnologicalMapProxy,
    UnitProxy,
} from "@me-team/host/main/graphql/types";
import {
    CombinedProductsInterface,
    CustomProductFromTechMapInterface,
    InterfaceProductWithTechMap,
    LocationWriteOffPageType,
    ProductListInterfaceForWriteOffPage,
    ProductToWriteOffCustomInterface,
    ReactSelectBrandsInterface,
    ReactSelectInterface,
    WriteOffSource,
} from "../../interfaces/interfaces";
import { TFunction } from "i18next";
import { DeepPartial } from "@apollo/client/utilities";

type Breadcrumb = { path: string; label: string };

enum ComeFromType {
    OPERATIONS = "OPERATIONS",
    RECORD = "RECORD",
    CLIENTS_HISTORY = "CLIENTS_HISTORY",
    UNKNOWN = "UNKNOWN",
}

class WriteOffService {
    public static productWithTechMapInterfaceInitialize(
        techMapList: DeepPartial<TechnologicalMapProxy[]>
    ): InterfaceProductWithTechMap[] {
        return (
            techMapList.map((techMap: TechnologicalMapProxy): InterfaceProductWithTechMap => {
                return {
                    techMapId: techMap.id,
                    techMapName: techMap.name,
                    techMapProductInfo: this.techMapProductInfoInterfaceInit(techMap.technologicalMapProductRelations),
                };
            }) || []
        );
    }

    public static productWithTechMapForUpdateInterfaceInitialize(
        techMapList: DeepPartial<StorageProductRelationHistoryProxy[]>
    ): InterfaceProductWithTechMap[] {
        const groupsByTechMapId: Map<number, InterfaceProductWithTechMap> = new Map();

        for (const relation of techMapList) {
            const techMapId: number = relation.techMap.id;

            let group: InterfaceProductWithTechMap | undefined = groupsByTechMapId.get(techMapId);
            if (!group) {
                const newGroup: InterfaceProductWithTechMap = {
                    techMapId,
                    techMapName: relation.techMap.name,
                    techMapProductInfo: [],
                };
                groupsByTechMapId.set(techMapId, newGroup);
                group = newGroup;
            }

            const productInfo: InterfaceProductWithTechMap["techMapProductInfo"][number] =
                this.techMapProductInfoForUpdateInterfaceInit(relation);

            group.techMapProductInfo.push(productInfo);
        }

        return Array.from(groupsByTechMapId.values());
    }

    public static addedProductForUpdateInit = (
        addedProductsList: DeepPartial<StorageProductRelationHistoryProxy[]>
    ): ProductListInterfaceForWriteOffPage[] => {
        return addedProductsList.map(
            (addedProduct: DeepPartial<StorageProductRelationHistoryProxy>): ProductListInterfaceForWriteOffPage => {
                return {
                    id: addedProduct.product.id,
                    name: addedProduct.product.name,
                    qty: addedProduct.operationQuantity,
                    storage: addedProduct.storage,
                    storages: null,
                    unit: addedProduct.operationUnit,
                    units: addedProduct.writeOffEditUnits,
                    categoryName: addedProduct.product.productCategoryNew.name,
                    sku: addedProduct.product.sku,
                    barcode: addedProduct.product.barcode,
                    brand: addedProduct.product.brand,
                    hasError: false,
                    errors: {
                        storageError: null,
                        qtyError: null,
                    },
                    isHidden: false,
                    isZeroAccept: true,
                };
            }
        );
    };

    public static productToWriteOffCustomInterfaceInit = (
        techMaps: DeepPartial<TechnologicalMapProxy[]>
    ): ProductToWriteOffCustomInterface[] => {
        return techMaps.flatMap((techMap) =>
            techMap.technologicalMapProductRelations.map((currentTechMap: TechnologicalMapProductRelationProxy) => {
                const singleStorage = this.getSingleStorage(currentTechMap.product.storages);

                return {
                    product: currentTechMap.product.id,
                    storage: singleStorage?.id ?? null,
                    quantity: currentTechMap.requiredQuantity,
                    unit: currentTechMap.unit.id,
                    techMapId: techMap.id,
                    isZeroAccept: false,
                };
            })
        );
    };

    public static productToUpdateWriteOffCustomInterfaceInit = (
        products: DeepPartial<StorageProductRelationHistoryProxy[]>
    ): ProductToWriteOffCustomInterface[] => {
        return products.map((item: DeepPartial<StorageProductRelationHistoryProxy>) => {
            return {
                product: item.product.id,
                storage: item.storage.id,
                quantity: item.operationQuantity,
                techMapId: item.techMap ? item.techMap.id : null,
                techMap: item.techMap ? item.techMap.id : null,
                unit: item.operationUnit.id,
                isZeroAccept: true,
            };
        });
    };

    public static productInputInit = (
        writeOffProductsList: ProductToWriteOffCustomInterface[]
    ): ProductWriteOffInput[] => {
        return writeOffProductsList.map((product: ProductToWriteOffCustomInterface) => {
            return {
                product: product.product,
                storage: product.storage,
                quantity: product.quantity,
                unit: product.unit,
                techMap: product.techMapId,
                isZeroAccept: false,
            };
        });
    };

    public static updateProductInputInit = (
        writeOffProductsList: ProductToWriteOffCustomInterface[]
    ): ProductWriteOffInput[] => {
        return writeOffProductsList.map((product: ProductToWriteOffCustomInterface) => {
            return {
                product: product.product,
                storage: product.storage,
                quantity: product.quantity,
                unit: product.unit,
                techMap: product.techMapId,
                isZeroAccept: product.isZeroAccept,
            };
        });
    };

    public static productListInterfaceInit = (
        products: DeepPartial<ProductProxy[]>,
        combinedProducts: CombinedProductsInterface
    ): ProductListInterfaceForWriteOffPage[] => {
        const techMapIds = new Set(
            Array.isArray(combinedProducts.productFromTechMap)
                ? combinedProducts.productFromTechMap.flatMap((techMap: InterfaceProductWithTechMap) =>
                      Array.isArray(techMap.techMapProductInfo)
                          ? techMap.techMapProductInfo.map((relation) => relation.id)
                          : []
                  )
                : []
        );

        const addedProductIds = new Set(
            Array.isArray(combinedProducts.addedProductToWriteOff)
                ? combinedProducts.addedProductToWriteOff.map((p) => p.id)
                : []
        );

        return products.map((product: ProductProxy) => {
            const isInEither = techMapIds.has(product.id) || addedProductIds.has(product.id);

            const singleStorage = this.getSingleStorage(product.storages);

            return {
                id: product.id,
                name: product.name,
                brand: product.brand,
                categoryName: product.productCategoryNew.name,
                sku: product.sku,
                barcode: product.barcode,
                unit: product.unit,
                units: product.units,
                storage: singleStorage,
                storages: product.storages,
                qty: null,
                isHidden: isInEither,
                hasError: false,
                errors: {
                    storageError: null,
                    qtyError: null,
                },
                isZeroAccept: false,
            };
        });
    };

    public static reactSelectBrands = (brandsList: ProductParametersProxy[]): ReactSelectBrandsInterface[] => {
        return (
            brandsList.map((brand) => {
                return {
                    label: brand.name,
                    value: brand.name,
                };
            }) || []
        );
    };

    public static reactSelectStorage = (storages: DeepPartial<StorageProxy[]>): ReactSelectInterface[] => {
        if (storages) {
            return storages.map((storage: StorageProxy) => {
                return {
                    label: storage.name,
                    value: storage.id,
                };
            });
        } else {
            return [];
        }
    };

    public static getSingleStorage = (storages: DeepPartial<StorageProxy[]>): DeepPartial<StorageProxy> | null => {
        return Array.isArray(storages) && storages.length === 1 ? storages[0] : null;
    };

    public static reactSelectUnits = (units: DeepPartial<UnitProxy[]>) => {
        if (units) {
            return units.map((unit) => {
                return {
                    label: unit.name,
                    value: unit.id,
                };
            });
        } else {
            return [];
        }
    };

    public static productFromTechMapValidation = (
        productsWithTechMap: InterfaceProductWithTechMap[],
        productsToWriteOff: ProductToWriteOffCustomInterface[],
        translate: TFunction<"translation">
    ): InterfaceProductWithTechMap[] => {
        if (productsWithTechMap && productsWithTechMap.length > 0) {
            return productsWithTechMap.map((techMap: InterfaceProductWithTechMap) => {
                const updatedTechMapProductInfo = techMap.techMapProductInfo.map(
                    (product: CustomProductFromTechMapInterface) => {
                        const currentProduct = productsToWriteOff.find(
                            (productItem) =>
                                productItem.product === product.product.id &&
                                productItem.techMapId === techMap.techMapId
                        );

                        const isQtyError = this.isQtyError(currentProduct.quantity, currentProduct.isZeroAccept);

                        if (currentProduct) {
                            return {
                                ...product,
                                hasError: !currentProduct.storage || isQtyError,
                                errors: {
                                    storageError: !currentProduct.storage ? translate("Select warehouse") : null,
                                    qtyError: isQtyError ? translate("Specify quantity") : null,
                                },
                            };
                        }

                        return {
                            ...product,
                            hasError: true,
                            errors: {
                                storageError: translate("Select warehouse"),
                                qtyError: translate("Specify quantity"),
                            },
                        };
                    }
                );

                return {
                    ...techMap,
                    techMapProductInfo: updatedTechMapProductInfo,
                };
            });
        }

        return [];
    };

    public static addedProductValidation = (
        products: ProductListInterfaceForWriteOffPage[],
        productsToWriteOff: ProductWriteOffInput[],
        translate: TFunction<"translation">
    ): ProductListInterfaceForWriteOffPage[] => {
        if (products && products.length > 0) {
            return products.map((product) => {
                const currentProduct = productsToWriteOff.find((productItem) => productItem.product === product.id);

                const isQtyError = this.isQtyError(currentProduct.quantity, currentProduct.isZeroAccept);

                if (currentProduct) {
                    return {
                        ...product,
                        hasError: !currentProduct.storage || isQtyError,
                        errors: {
                            storageError: !currentProduct.storage ? translate("Select warehouse") : null,
                            qtyError: isQtyError ? translate("Specify quantity") : null,
                        },
                    };
                }
                return {
                    ...product,
                };
            });
        }
        return [];
    };

    public static breadcrumbsFromPage = (
        location: LocationWriteOffPageType,
        branchId: string,
        translate: TFunction<"translation", undefined>,
        writeOffSource?: WriteOffSource
    ): Breadcrumb[] => {
        const routes: Record<ComeFromType, (branchId: string, t: TFunction<"translation", undefined>) => Breadcrumb[]> =
            {
                [ComeFromType.OPERATIONS]: (branchId, t) => [
                    { path: `/warehouses/operations/${branchId}`, label: t("Warehouse operations") },
                ],
                [ComeFromType.RECORD]: (branchId, t) => [
                    { path: `/logbook/journal/${branchId}`, label: t("Logbook Journal") },
                ],
                [ComeFromType.CLIENTS_HISTORY]: (branchId, t) => [
                    { path: `/clients-history-visit/${branchId}`, label: t("Visits History") },
                ],
                [ComeFromType.UNKNOWN]: (branchId, t) => [
                    { path: `/warehouses/operations/${branchId}`, label: t("Warehouse operations") },
                ],
            };
        const detectComeFrom = (location: LocationWriteOffPageType): ComeFromType => {
            const query = new URLSearchParams(location.search);
            const isFromLogbook = query.get("isFromLogbook");
            const cf = location.state?.comeFrom;
            if (!cf && !isFromLogbook) {
                return writeOffSource === "logbook" ? ComeFromType.RECORD : ComeFromType.OPERATIONS;
            }
            if (cf?.isFromOperations) return ComeFromType.OPERATIONS;
            if (cf?.isFromRecord || isFromLogbook) return ComeFromType.RECORD;
            if (cf?.isFromClientsHistory) return ComeFromType.CLIENTS_HISTORY;
            return ComeFromType.UNKNOWN;
        };

        const from = detectComeFrom(location);
        switch (from) {
            case ComeFromType.OPERATIONS:
                return routes[ComeFromType.OPERATIONS](branchId, translate);
            case ComeFromType.RECORD:
                return routes[ComeFromType.RECORD](branchId, translate);
            case ComeFromType.CLIENTS_HISTORY:
                return routes[ComeFromType.CLIENTS_HISTORY](branchId, translate);
            default:
                return routes[ComeFromType.UNKNOWN](branchId, translate);
        }
    };

    private static isQtyError = (qty: number | null, isZeroAccept: boolean): boolean => {
        return qty == null || Number.isNaN(qty) || (isZeroAccept ? qty < 0 : qty <= 0);
    };

    private static techMapProductInfoInterfaceInit = (
        relations: TechnologicalMapProductRelationProxy[]
    ): CustomProductFromTechMapInterface[] => {
        return relations.map((item) => {
            const singleStorage = this.getSingleStorage(item.product.storages);

            return {
                id: item.id,
                product: {
                    id: item.product.id,
                    name: item.product.name,
                    storage: singleStorage,
                    storages: item.product.storages,
                    units: item.product.units,
                },
                requiredQuantity: item.requiredQuantity,
                relationsUnit: item.unit,
                hasError: false,
                errors: {
                    storageError: null,
                    qtyError: null,
                },
                isZeroAccept: false,
            };
        });
    };

    private static techMapProductInfoForUpdateInterfaceInit = (
        productInfo: DeepPartial<StorageProductRelationHistoryProxy>
    ): CustomProductFromTechMapInterface => {
        return {
            id: productInfo.id,
            product: {
                id: productInfo.product.id,
                name: productInfo.product.name,
                storage: productInfo.storage,
                storages: null,
                units: productInfo.writeOffEditUnits,
            },
            requiredQuantity: productInfo.operationQuantity,
            relationsUnit: productInfo.operationUnit,
            hasError: false,
            errors: {
                storageError: null,
                qtyError: null,
            },
            isZeroAccept: true,
        };
    };
}

export default WriteOffService;
